import type { LessonStandard } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type EmberStore from "cc-frontend/services/ember-store"
import { useCallback, useEffect, useRef } from "react"
import { useEmberService } from "react-app/providers/ember-service-context"

/** A standard as the CSP standard sets store it (not the shape cards keep). */
interface CspStandard {
  id: string
  description: string
  statementNotation: string
  altStatementNotation?: string
}

interface StandardSetModel {
  id: string
  attributes: {
    title: string
    subject: string
    jurisdiction: { title: string }
    standards: Record<string, CspStandard>
  }
}

/**
 * Loads the course's standard sets and returns a synchronous lookup by standard id — what the
 * assistant names a standard by, leaving the rest for us to fill in.
 *
 * The sets are fetched on mount so the lookup can answer during a stream; a standard whose set
 * hasn't arrived yet reads as unknown, as does one from outside the course's sets.
 */
export function useCourseStandards(course: Course): (standardId: string) => LessonStandard | null {
  const store = useEmberService<EmberStore>("ember-store")
  const standardSetsRef = useRef<StandardSetModel[]>([])
  const standardSetIds = course.attributes.standardSetIds

  useEffect(() => {
    let isMounted = true
    for (const standardSetId of standardSetIds) {
      Promise.resolve(store.find("standard-set", standardSetId)).then(
        (standardSet: StandardSetModel | null) => {
          if (isMounted && standardSet) standardSetsRef.current.push(standardSet)
        },
        (error: unknown) => console.error(`Failed to load standard set ${standardSetId}`, error),
      )
    }
    return () => {
      isMounted = false
    }
  }, [store, standardSetIds])

  return useCallback((standardId: string) => {
    for (const standardSet of standardSetsRef.current) {
      const standard = standardSet.attributes.standards[standardId]
      if (standard) {
        return {
          id: standard.id,
          code: standard.altStatementNotation || standard.statementNotation,
          statement: standard.description,
          jurisdiction: standardSet.attributes.jurisdiction.title,
          grades: standardSet.attributes.title,
          standardSetId: standardSet.id,
          subject: standardSet.attributes.subject,
        }
      }
    }
    return null
  }, [])
}
